"use client";

/**
 * Helpers + inline badge for rendering AI-suggested field changes on recap cells.
 *
 * Reads the `flow.flow_metadata.ai_suggestions[]` array (with legacy
 * `ai_suggestion` single-block fallback) and surfaces a "from → new"
 * indicator next to each affected value, driven by the **latest pending
 * entry**. Resolved entries (accepted / declined / modified) are part of
 * the audit trail but don't paint cells violet.
 *
 * Cells call:
 *   - `isAiPendingField` — apply violet background while unresolved
 *   - `getAiChangeValues` — pull `{ from, to }` to render inline preview
 *   - `<AiChangeBadge>` — tiny inline pill
 *
 * Resolution is server-driven: the V3 approve/decline endpoints flip the
 * latest pending entry; the next sync push rerenders cells without the
 * violet chrome.
 */

import type { PreOrderFlow } from "@/app/api/types/preOrder";
import {
  type AiSuggestion,
  PreOrderFlowStatus,
} from "@/app/api/types/preOrderFlow";

const AI_SUGGESTIONS_KEY = "ai_suggestions";
const AI_SUGGESTION_LEGACY_KEY = "ai_suggestion";

export function readBlocks(flow: PreOrderFlow): AiSuggestion[] {
  const meta = flow.flow_metadata as Record<string, unknown> | null | undefined;
  if (!meta || typeof meta !== "object") return [];
  const arr = (meta as Record<string, unknown>)[AI_SUGGESTIONS_KEY];
  if (Array.isArray(arr)) {
    return arr.filter(isWellFormed) as AiSuggestion[];
  }
  const legacy = (meta as Record<string, unknown>)[AI_SUGGESTION_LEGACY_KEY];
  if (legacy && typeof legacy === "object" && !Array.isArray(legacy)) {
    const wrapped = wrapLegacy(legacy as Record<string, unknown>);
    if (wrapped) return [wrapped];
  }
  return [];
}

function isWellFormed(x: unknown): x is AiSuggestion {
  if (!x || typeof x !== "object") return false;
  const o = x as Partial<AiSuggestion>;
  return (
    typeof o.index === "number" &&
    Number.isInteger(o.index) &&
    typeof o.batchId === "string" &&
    typeof o.proposedBy === "string" &&
    typeof o.proposedAt === "string" &&
    typeof o.status === "string" &&
    typeof o.type === "string"
  );
}

function wrapLegacy(legacy: Record<string, unknown>): AiSuggestion | null {
  if (
    typeof legacy.batchId !== "string" ||
    typeof legacy.proposedBy !== "string" ||
    typeof legacy.proposedAt !== "string" ||
    typeof legacy.status !== "string" ||
    typeof legacy.type !== "string"
  ) {
    return null;
  }
  const out = {
    index: 1,
    status: legacy.status,
    type: legacy.type,
    batchId: legacy.batchId,
    proposedBy: legacy.proposedBy,
    proposedAt: legacy.proposedAt,
  } as AiSuggestion;
  if (legacy.changes && typeof legacy.changes === "object") {
    (out as AiSuggestion).changes = legacy.changes as AiSuggestion["changes"];
  }
  return out;
}

/**
 * Position-based: returns the LAST entry only if its status is `"pending"`.
 * Older pending entries (audit traces from prior AI re-proposals) don't
 * count — only the latest intent paints the cell violet.
 */
export function latestPending(blocks: AiSuggestion[]): AiSuggestion | null {
  if (blocks.length === 0) return null;
  const last = blocks[blocks.length - 1];
  return last.status === "pending" ? last : null;
}

function readFieldChange(
  suggestion: AiSuggestion,
  field: string,
): { from: unknown; to: unknown } | null {
  // The AI tool stages field keys in camelCase (`unitId`, `pricePerWeight`,
  // `productId`, `hiddenComment`) because the wire schema is camelCase. Cells
  // historically pass snake_case (`unit_id`, `price_per_weight`, …) since
  // that's the DB column name. Look up both so a snake-case caller resolves
  // to the camelCase JSONB key — and a legacy snake-case JSONB entry
  // (if any) still resolves to a snake-case caller.
  const changes = suggestion.changes;
  if (!changes) return null;
  const direct = changes[field];
  const fromCamel = !field.includes("_")
    ? undefined
    : changes[snakeToCamel(field)];
  const change = direct ?? fromCamel;
  if (!change || typeof change !== "object" || !("to" in change)) return null;
  return { from: change.from, to: change.to };
}

export function snakeToCamel(field: string): string {
  return field.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

/**
 * True when the cell wrapper should render a violet background — the
 * latest pending entry is a modify on this field.
 */
export function isAiPendingField(flow: PreOrderFlow, field: string): boolean {
  const suggestion = latestPending(readBlocks(flow));
  if (!suggestion) return false;
  if (suggestion.type !== "modify") return false;
  return readFieldChange(suggestion, field) != null;
}

/**
 * Returns the pending `{ from, to }` for `field`, or null when the
 * latest pending entry is missing / not a modify on this field.
 */
export function getAiChangeValues(
  flow: PreOrderFlow,
  field: string,
): { from: unknown; to: unknown } | null {
  const suggestion = latestPending(readBlocks(flow));
  if (!suggestion) return null;
  if (suggestion.type !== "modify") return null;
  return readFieldChange(suggestion, field);
}

/** Light violet cell-wrapper bg for pending AI-modified fields. */
export const AI_PENDING_CELL_CLASS = "bg-violet-50/80 rounded";

/**
 * Wrapper chrome for a pending AI-modified HEADER field: violet wash PLUS a
 * violet border (with a hair of padding so the border frames the control
 * instead of doubling its own edge). Distinct from `AI_PENDING_CELL_CLASS`
 * (the row-cell background-only tint) because header fields are larger,
 * standalone controls where a border reads far more clearly than a tint.
 */
export const AI_PENDING_FIELD_CLASS =
  "bg-violet-50/80 border border-violet-300 rounded p-0.5";

interface AiChangeBadgeProps {
  flow: PreOrderFlow;
  // Field names must match the camelCase keys the API writes into
  // `flow_metadata.ai_suggestions[].changes` (see `FLOW_COLUMNS` in
  // `apps/api/src/domains/ai/pre-order/repository.ts`). Using snake_case
  // here silently fails: `getAiChangeValues` returns null and no overlay
  // renders. `weight`/`quantity`/`comment` happen to be identical in both
  // conventions; the rest must be camelCase.
  field:
    | "quantity"
    | "weight"
    | "pricePerWeight"
    | "unitId"
    | "productId"
    | "comment";
  /** Optional formatter (e.g. for currency / units). Defaults to a number-aware stringifier. */
  format?: (value: unknown) => string;
}

export function defaultFormat(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : String(Number(value));
  }
  return String(value);
}

interface AiDiffRowProps {
  fromText: string;
  toText: string;
  testId?: string;
}

/**
 * Shared "from → new" diff row: struck-through live value, arrow, and the bold
 * proposed value in the AI violet accent. The single home for this styling —
 * reused by the built-in-field badge and the flat-metadata custom field/column
 * badge so the accent lives in one place.
 */
export function AiDiffRow({ fromText, toText, testId }: AiDiffRowProps) {
  return (
    <div
      className="mt-0.5 flex items-center gap-1 text-[10px] font-medium text-violet-700 leading-none"
      title={`AI suggests: ${fromText} → ${toText}`}
      data-testid={testId}
    >
      <span className="line-through text-violet-400">{fromText}</span>
      <span className="text-violet-600">→</span>
      <span className="font-bold text-violet-800">{toText}</span>
    </div>
  );
}

export function AiChangeBadge({ flow, field, format }: AiChangeBadgeProps) {
  const change = getAiChangeValues(flow, field);
  if (!change) return null;

  const fmt = format ?? defaultFormat;
  return <AiDiffRow fromText={fmt(change.from)} toText={fmt(change.to)} />;
}

/**
 * Return the latest pending AI suggestion entry attached to a flow, or
 * null. Resolved entries (history) live in the array but don't surface
 * inline cell chrome.
 */
export function getAiSuggestion(flow: PreOrderFlow): AiSuggestion | null {
  return latestPending(readBlocks(flow));
}

// ---------- Staged-metadata-blob helpers (m3_comments selector) ----------
//
// The AI tool stages free-form metadata patches as a single sentinel field
// in the suggestion block: `changes.flowMetadata = {from, to}` (per-flow)
// or `changes.preOrderMetadata = {from, to}` (header). The `to` value is
// the AI's full intended `flow_metadata` / `pre_order_metadata` blob —
// includes `m3_comments`, `nb_colis`, `ai`, `delineation`, `custom_fields`,
// etc. We expose ONLY `m3_comments` to the UI: it's the user-visible
// ERP slot, the others are either internal markers or already surfaced
// by their own dedicated cells. Future keys add an opt-in entry here;
// unknown keys stay invisible by default.

// Exported so the header banner can share a single source-of-truth — these
// must stay byte-for-byte in sync with the dispatcher's constants
// (`apps/api/src/domains/ai/pre-order/dispatcher.ts`).
export const FLOW_METADATA_BLOB_FIELD = "flowMetadata";
export const HEADER_METADATA_BLOB_FIELD = "preOrderMetadata";

/**
 * Read a single sub-key (e.g. `m3_comments`) out of the staged metadata-
 * blob field's `{from, to}` pair. Returns `null` when the latest pending
 * entry doesn't carry the blob field, or when the sub-key is absent on
 * BOTH sides (no diff to surface).
 */
function readMetadataBlobChange(
  suggestion: AiSuggestion,
  blobField: string,
  subKey: string,
): { from: unknown; to: unknown } | null {
  const change = suggestion.changes?.[blobField];
  if (!change || typeof change !== "object" || !("to" in change)) return null;
  const fromObj =
    change.from &&
    typeof change.from === "object" &&
    !Array.isArray(change.from)
      ? (change.from as Record<string, unknown>)
      : null;
  const toObj =
    change.to && typeof change.to === "object" && !Array.isArray(change.to)
      ? (change.to as Record<string, unknown>)
      : null;
  const fromVal = fromObj ? (fromObj[subKey] ?? null) : null;
  const toVal = toObj ? (toObj[subKey] ?? null) : null;
  if (fromVal == null && toVal == null) return null;
  return { from: fromVal, to: toVal };
}

/**
 * Pending m3_comments diff for a flow. Reads `changes.flowMetadata.{from,to}`
 * on the latest pending modify and extracts the `m3_comments` slot.
 * Returns `null` when no pending m3_comments change exists.
 */
export function getFlowM3CommentsAiChange(
  flow: PreOrderFlow,
): { from: unknown; to: unknown } | null {
  const suggestion = latestPending(readBlocks(flow));
  if (!suggestion || suggestion.type !== "modify") return null;
  return readMetadataBlobChange(
    suggestion,
    FLOW_METADATA_BLOB_FIELD,
    "m3_comments",
  );
}

/** True when the per-flow CommentCell should light up the violet pulse for m3_comments. */
export function isAiPendingFlowM3Comments(flow: PreOrderFlow): boolean {
  return getFlowM3CommentsAiChange(flow) != null;
}

/**
 * Pending m3_comments diff for a pre-order header. Mirrors the per-flow
 * extractor, but reads from `pre_order_metadata.ai_suggestions[*].changes
 * .preOrderMetadata` instead.
 */
export function getHeaderM3CommentsAiChange(
  metadata: Record<string, unknown> | null | undefined,
): { from: unknown; to: unknown } | null {
  const blocks = readBlocksFromMetadata(metadata);
  const suggestion = latestPending(blocks);
  if (!suggestion || suggestion.type !== "modify") return null;
  return readMetadataBlobChange(
    suggestion,
    HEADER_METADATA_BLOB_FIELD,
    "m3_comments",
  );
}

/** True when the header CommentPopover should surface the violet m3_comments pulse. */
export function isAiPendingHeaderM3Comments(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  return getHeaderM3CommentsAiChange(metadata) != null;
}

/**
 * Generic header-field AI change reader — the header-level mirror of
 * `getAiChangeValues(flow, field)`. Operates on a `pre_order_metadata`
 * object and the header's `ai_suggestions[]` array. `field` may be passed
 * snake_case (DB column, e.g. `delivery_date`) or camelCase (wire key,
 * e.g. `deliveryDate`) — `readFieldChange` resolves both. Returns the
 * latest pending modify's `{ from, to }` for that field, or null.
 */
export function getHeaderAiChangeValues(
  metadata: Record<string, unknown> | null | undefined,
  field: string,
): { from: unknown; to: unknown } | null {
  const suggestion = latestPending(readBlocksFromMetadata(metadata));
  if (!suggestion || suggestion.type !== "modify") return null;
  return readFieldChange(suggestion, field);
}

/** True when the header's latest pending modify entry touches `field`. */
export function isAiPendingHeaderField(
  metadata: Record<string, unknown> | null | undefined,
  field: string,
): boolean {
  return getHeaderAiChangeValues(metadata, field) != null;
}

/**
 * Sub-keys of `m3_comments` we surface in the diff. Order matters —
 * drives display ordering. Shared with `AiHeaderChangesBanner.tsx` so
 * per-flow + header diffs label the same slots in the same order.
 */
export const M3_KEYS = [
  "c1",
  "c2",
  "c3",
  "c4",
  "c5",
  "qualite",
  "description",
] as const;

interface M3SlotChange {
  key: (typeof M3_KEYS)[number];
  from: string;
  to: string;
}

/**
 * Compute the per-slot diff of an m3_comments `{from, to}` pair. Returns
 * only the slots whose value actually moved — empty arrays collapse the
 * cell to nothing rather than rendering "no change". Empty/absent
 * values display as "—" so a cleared slot stays visible (vs. silently
 * disappearing).
 */
export function diffM3CommentSlots(change: {
  from: unknown;
  to: unknown;
}): M3SlotChange[] {
  const fromObj = readM3CommentsObj(change.from);
  const toObj = readM3CommentsObj(change.to);
  const out: M3SlotChange[] = [];
  for (const key of M3_KEYS) {
    const a = fromObj?.[key] ?? "";
    const b = toObj?.[key] ?? "";
    if (a === b) continue;
    out.push({ key, from: a || "—", to: b || "—" });
  }
  return out;
}

/**
 * Coerce a raw `m3_comments` value to a flat string→string map. Returns
 * null when the value is absent or malformed (not an object).
 *
 * Exported so the header banner can share a single coercion path —
 * keeps badge + banner labelling identical when the AI stages partial
 * (or empty) slot sets.
 */
export function readM3CommentsObj(
  value: unknown,
): Record<string, string> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const obj = value as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const key of M3_KEYS) {
    const v = obj[key];
    if (typeof v === "string") out[key] = v;
    else if (v != null) out[key] = String(v);
    else out[key] = "";
  }
  return out;
}

interface AiM3CommentsBadgeProps {
  flow: PreOrderFlow;
}

/**
 * Inline diff badge for per-flow m3_comments AI proposals. Mirrors
 * `<AiChangeBadge>` for column fields (same violet styling, same
 * line-through `from` / arrow / bold `to` layout) but iterates the
 * m3_comments sub-keys (c1-c5, qualite, description) and renders only
 * the slots that actually moved. Renders nothing when no pending
 * m3_comments diff exists, so callers can drop it in unconditionally
 * next to the popover without conditional gating.
 */
export function AiM3CommentsBadge({ flow }: AiM3CommentsBadgeProps) {
  const change = getFlowM3CommentsAiChange(flow);
  if (!change) return null;
  const slots = diffM3CommentSlots(change);
  if (slots.length === 0) return null;

  const tooltip = `AI suggests: ${slots
    .map((s) => `${s.key} ${s.from} → ${s.to}`)
    .join(", ")}`;

  return (
    <div
      className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[10px] font-medium text-violet-700 leading-none"
      title={tooltip}
    >
      <span className="text-violet-500">M3:</span>
      {slots.map((s) => (
        <span key={s.key} className="flex items-center gap-0.5">
          <span className="text-violet-400">{s.key}</span>
          <span className="line-through text-violet-400">{s.from}</span>
          <span className="text-violet-600">→</span>
          <span className="font-bold text-violet-800">{s.to}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Header-side mirror of `readBlocks(flow)`: same legacy single-block
 * fallback, but operates on a metadata object directly so callers don't
 * need a flow row. Lives here (next to the per-flow reader) so the legacy
 * compat shape stays in one place.
 */
function readBlocksFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): AiSuggestion[] {
  if (!metadata || typeof metadata !== "object") return [];
  const arr = (metadata as Record<string, unknown>)[AI_SUGGESTIONS_KEY];
  if (Array.isArray(arr)) {
    return arr.filter(isWellFormed) as AiSuggestion[];
  }
  const legacy = (metadata as Record<string, unknown>)[
    AI_SUGGESTION_LEGACY_KEY
  ];
  if (legacy && typeof legacy === "object" && !Array.isArray(legacy)) {
    const wrapped = wrapLegacy(legacy as Record<string, unknown>);
    if (wrapped) return [wrapped];
  }
  return [];
}

/**
 * True when the flow has any pending AI signal that should surface UI
 * (pending entry in metadata OR whole-line AI add via flow.status=AI_PROPOSAL).
 */
export function flowHasPendingAi(flow: PreOrderFlow): boolean {
  if (latestPending(readBlocks(flow))) return true;
  return flow.status === PreOrderFlowStatus.AI_PROPOSAL;
}
